import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../css/news.css';
import '../css/news-carousel.css';
import news1 from '../assets/carousel-1.jpg';
import news2 from '../assets/carousel-2.jpg';
import news3 from '../assets/carousel-3.jpg';
import { getNews } from '../utils/newsStorage';

const defaultNews = [
    {
        id: 1,
        title: "D.R.Y. Sovereignty Recognized by Global Alliances",
        date: "April 15, 2024",
        category: "Politics",
        featuredImage: news1,
        excerpt: "The Democratic Republic of the Yoruba receives acknowledgment from international bodies following the historic proclamation.",
        authorisedBy: "Executive Office"
    },
    {
        id: 2,
        title: "Infrastructure Development Plan Unveiled",
        date: "May 10, 2024",
        category: "Development",
        featuredImage: news2,
        excerpt: "The provisional government announces a comprehensive roadmap for rebuilding roads, power, and healthcare."
    },
    {
        id: 3,
        title: "Cultural Renaissance Festival Announced",
        date: "May 22, 2024",
        category: "Culture",
        featuredImage: news3,
        excerpt: "A week-long celebration of Yoruba heritage, arts, and music to take place in the capital."
    },
    {
        id: 4,
        title: "New Education Policy for Indigenous Youths",
        date: "June 01, 2024",
        category: "Education",
        featuredImage: news1,
        excerpt: "Free and compulsory education initiative launched to empower the next generation of leaders."
    }
];

export default function NewsUpdates({ maxItems }) {
    const [items, setItems] = useState(defaultNews);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const autoPlayRef = useRef(null);

    useEffect(() => {
        const load = async () => {
            try {
                const stored = await getNews();
                if (stored && stored.length > 0) {
                    console.log('📰 Loaded news from API:', stored.length, 'items');
                    setItems(stored);
                } else {
                    console.warn('⚠️  No items from API, using defaults');
                }
            } catch (err) {
                console.error("Failed to load news items", err);
            }
        };
        load();
    }, []);

    // Detect screen size for responsive behavior
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const resolvedItems = items.map((item, index) => {
        if (item.featuredImage || item.image) return item;
        const fallback = [news1, news2, news3][index % 3];
        return { ...item, featuredImage: fallback };
    });

    // Calculate cards per view based on screen size
    const cardsPerView = isMobile ? 1 : isTablet ? 2 : 3;
    
    // If maxItems is defined, use that; otherwise show all with carousel
    const useCarousel = maxItems === undefined;
    const itemLimit = maxItems !== undefined ? maxItems : resolvedItems.length;
    const displayedItems = resolvedItems.slice(0, itemLimit);
    
    // For homepage (maxItems defined), show featured + carousel
    const featuredNews = displayedItems[0] || null;
    const otherNews = featuredNews ? displayedItems.slice(1) : displayedItems;
    
    // Calculate total slides for carousel
    const totalSlides = Math.ceil(otherNews.length / cardsPerView);
    const showViewAll = resolvedItems.length > itemLimit && maxItems !== undefined;

    // Auto-play carousel
    useEffect(() => {
        if (!useCarousel || !isAutoPlay || otherNews.length <= cardsPerView) {
            return;
        }

        const startAutoPlay = () => {
            autoPlayRef.current = setInterval(() => {
                setCurrentSlide(prev => (prev + 1) % totalSlides);
            }, 5000);
        };

        startAutoPlay();
        return () => {
            if (autoPlayRef.current) {
                clearInterval(autoPlayRef.current);
            }
        };
    }, [useCarousel, isAutoPlay, otherNews.length, cardsPerView, totalSlides]);

    const handlePrev = () => {
        setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
        setIsAutoPlay(false);
        setTimeout(() => setIsAutoPlay(true), 7000); // Resume auto-play after 7s
    };

    const handleNext = () => {
        setCurrentSlide(prev => (prev + 1) % totalSlides);
        setIsAutoPlay(false);
        setTimeout(() => setIsAutoPlay(true), 7000); // Resume auto-play after 7s
    };

    const handleMouseEnter = () => {
        setIsAutoPlay(false);
    };

    const handleMouseLeave = () => {
        setIsAutoPlay(true);
    };

    // Get current slide cards
    const startIdx = currentSlide * cardsPerView;
    const currentCards = otherNews.slice(startIdx, startIdx + cardsPerView);

    return (
        <section className="news-section">
            <div className="container">
                <div className="news-header">
                    <h2 className="section-title">Latest News & Updates</h2>
                    <p className="lead">Stay informed about the progress and announcements of the D.R.Y. Government.</p>
                </div>

                {featuredNews && (
                    <div className="featured-news">
                        <div className="news-card">
                            <div className="news-img-wrapper">
                                <img src={featuredNews.featuredImage} alt={featuredNews.title} />
                                <span className="news-category">{featuredNews.category}</span>
                            </div>
                            <div className="news-content">
                                <div className="news-meta">
                                    <i className="far fa-calendar-alt"></i> {featuredNews.date}
                                    {featuredNews.authorisedBy && (
                                        <span className="news-author"> • Authorised by {featuredNews.authorisedBy}</span>
                                    )}
                                </div>
                                <h3 className="news-title">{featuredNews.title}</h3>
                                <p className="news-excerpt">{featuredNews.excerpt}</p>
                                <Link to={`/news/${featuredNews.id}`} className="news-link">
                                    Read Full Story <i className="fas fa-arrow-right"></i>
                                </Link>
                            </div>
                        </div>
                    </div>
                )}

                {/* News Carousel for Homepage */}
                {useCarousel && otherNews.length > 0 ? (
                    <div 
                        className="news-carousel-wrap"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        <div className="news-carousel-container">
                            <div className="news-carousel">
                                <div 
                                    className="news-carousel-track"
                                    style={{
                                        transform: `translateX(calc(-${currentSlide * 100}% - ${currentSlide * 20}px))`,
                                        transition: 'transform 0.6s ease-in-out'
                                    }}
                                >
                                    {otherNews.map((news, index) => (
                                        <div key={news.id} className="news-carousel-item">
                                            <div className="news-card">
                                                <div className="news-img-wrapper">
                                                    <img src={news.featuredImage} alt={news.title} />
                                                    <span className="news-category">{news.category}</span>
                                                </div>
                                                <div className="news-content">
                                                    <div className="news-meta">
                                                        <i className="far fa-calendar-alt"></i> {news.date}
                                                        {news.authorisedBy && (
                                                            <span className="news-author"> • Authorised by {news.authorisedBy}</span>
                                                        )}
                                                    </div>
                                                    <h3 className="news-title">{news.title}</h3>
                                                    <p className="news-excerpt">{news.excerpt}</p>
                                                    <Link to={`/news/${news.id}`} className="news-link">
                                                        Read More <i className="fas fa-arrow-right"></i>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Carousel Controls */}
                            {totalSlides > 1 && (
                                <>
                                    <button 
                                        className="carousel-btn carousel-btn-prev"
                                        onClick={handlePrev}
                                        aria-label="Previous news"
                                    >
                                        <i className="fas fa-chevron-left"></i>
                                    </button>
                                    <button 
                                        className="carousel-btn carousel-btn-next"
                                        onClick={handleNext}
                                        aria-label="Next news"
                                    >
                                        <i className="fas fa-chevron-right"></i>
                                    </button>

                                    {/* Dot indicators */}
                                    <div className="carousel-dots">
                                        {Array.from({ length: totalSlides }).map((_, idx) => (
                                            <button
                                                key={idx}
                                                className={`carousel-dot ${idx === currentSlide ? 'active' : ''}`}
                                                onClick={() => {
                                                    setCurrentSlide(idx);
                                                    setIsAutoPlay(false);
                                                    setTimeout(() => setIsAutoPlay(true), 7000);
                                                }}
                                                aria-label={`Go to slide ${idx + 1}`}
                                            ></button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                ) : (
                    // Static grid for View All page
                    <div className="row g-4">
                        {currentCards.map(news => (
                            <div key={news.id} className={`col-lg-${12/cardsPerView} col-md-6`}>
                                <div className="news-card">
                                    <div className="news-img-wrapper">
                                        <img src={news.featuredImage} alt={news.title} />
                                        <span className="news-category">{news.category}</span>
                                    </div>
                                    <div className="news-content">
                                        <div className="news-meta">
                                            <i className="far fa-calendar-alt"></i> {news.date}
                                            {news.authorisedBy && (
                                                <span className="news-author"> • Authorised by {news.authorisedBy}</span>
                                            )}
                                        </div>
                                        <h3 className="news-title">{news.title}</h3>
                                        <p className="news-excerpt">{news.excerpt}</p>
                                        <Link to={`/news/${news.id}`} className="news-link">
                                            Read More <i className="fas fa-arrow-right"></i>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {showViewAll && (
                    <div className="text-center mt-4">
                        <Link to="/news" className="btn btn-primary">
                            View All News
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}
